import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Controller, useForm } from "react-hook-form";
import { MDBCol, MDBRow } from "mdbreact";

import "./Corrections.scss";

import { AbsCheckBox, Input, Label } from "abs-ui-components";

import axiosHttp from "../../../services/interceptor";
import { emitToasterMsg } from "../../../services/toasterUtility";
import { GetReportFlagsByStatus } from "../services";
import { reportsActions } from "../../../store/reports/reports.slice";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const CorrectionRequestForm = ({
	toggleCorrection,
	reportDetails,
	isRepliedCorrection,
	parentCorrectionData,
	fetchData, // Receiving the callback function
}) => {
	const {
		control,
		handleSubmit,
		setValue,
		trigger,
		formState: { errors },
		watch,
	} = useForm();
	const [childReportsIdList, setChildReportsIdList] = useState([]);
	const [reportIdToMstIdMap, setReportIdToMstIdMap] = useState({});
	const [disableButtons, setDisableButtons] = useState(false);
	const dispatch = useDispatch();

	const handleCheckboxChange = (field) => (event) => {
		field.onChange(event.target.checked);
	};

	const handleCorrectionForm = (formdata) => {
		setDisableButtons(true);
		if (!isRepliedCorrection) {
			const applicableReports = [];
			const correctionTypes = [];

			Object?.keys(formdata).forEach((key) => {
				if ((key?.includes("DCS") || key?.includes("EUMRV") || key?.includes("UKMRV")) && formdata[key]) {
					applicableReports.push(reportIdToMstIdMap[key]);
				} else if (formdata[key] && (key === "Vessel Details" || key === "Reported Data" || key === "Supporting Documents")) {
					correctionTypes.push(key);
				}
			});

			const payload = {
				reportMstId: reportDetails.ReportMstId,
				correctionHeader: formdata.textCorrection,
				correctionMessage: formdata.replyMessage,
				correctionTypes: correctionTypes,
				applicableReportIds: applicableReports,
			};

			axiosHttp({
				url: `${BASE_URL}api/Corrections/AddCorrection`,
				method: "POST",
				data: payload,
			})
				.then((res) => {
					setDisableButtons(false);
					emitToasterMsg("success", "Correction Request", "Correction Request Raised Successfully");
					toggleCorrection(true);
					fetchData(); // Call fetchData after successful request
				})
				.catch((err) => {
					setDisableButtons(false);
					emitToasterMsg("error", "Error", err.message);
				})
				.finally(() => {
					GetReportFlagsByStatus(reportDetails?.ReportMstId).then((response) => {						
						dispatch(
							reportsActions.setReportFlagDetails({
								reportFlagDetails: response,
							})
						);
					});
				});
		} else {
			const repliedMsgPayload = {
				reportCorrectionsDtlId: parentCorrectionData.reportCorrectionsDtlId,
				message: formdata.replyMessage,
			};

			axiosHttp({
				url: `${BASE_URL}api/Corrections/AddCorrectionMessage`,
				method: "POST",
				data: repliedMsgPayload,
			})
				.then((res) => {
					setDisableButtons(false);
					if (res?.data?.isSuccess) {
						emitToasterMsg("success", "Correction Reply Message", "Reply Sent Successfully");
						toggleCorrection(true);
						fetchData(); // Call fetchData after successful request
					}
				})
				.catch((err) => {
					setDisableButtons(false);
					emitToasterMsg("error", "Error", err?.message);
				});
		}
	};

	const getChildReportIdList = async (reportMstId) => {
		try {
			const response = await axiosHttp.get(`api/Corrections/GetReportChildren?reportMstId=${reportMstId}`);

			const childReports = response?.data?.data;
			setChildReportsIdList(childReports || []);

			// Create a map from reportId to reportMstId
			const reportIdToMstId = {};
			childReports.forEach((item) => {
				reportIdToMstId[item.reportid] = item.reportMstId;
			});
			setReportIdToMstIdMap(reportIdToMstId);
		} catch (error) {
			emitToasterMsg(error.message);
		}
	};

	useEffect(() => {
		if (reportDetails) {
			const reportMstId = reportDetails.ReportMstId;
			getChildReportIdList(reportMstId);
		}
	}, [reportDetails]);

	useEffect(() => {
		if (isRepliedCorrection && parentCorrectionData.ReportCorrectionsAppliedDtls && parentCorrectionData.ReportCorrectionsTypeDtls) {
			const applicableReports = parentCorrectionData.ReportCorrectionsAppliedDtls.map((item) => item.ReportId);
			applicableReports.forEach((report) => {
				setValue(report, true);
			});

			const correctionTypes = parentCorrectionData.ReportCorrectionsTypeDtls.map((item) => item.CorrectionType);
			correctionTypes.forEach((report) => {
				setValue(report, true);
			});
		}
	}, [isRepliedCorrection, parentCorrectionData, setValue]);

	const handleBlur = async (field, value) => {
		await trigger(field);
	};

	// Watch for the checkbox values
	const watchCheckboxes = watch(["Reported Data"]);
	const watchApplicableReports = watch(childReportsIdList.map((item) => item.reportid));
	const isAtLeastOneChecked = watchCheckboxes.some((value) => value);
	const isAtLeastOneApplicableReportChecked = watchApplicableReports.some((value) => value);

	return (
		<div className="container-fluid modal-height">
			<form onSubmit={handleSubmit(handleCorrectionForm)}>
				<MDBRow>
					{isRepliedCorrection ? (
						""
					) : (
						<MDBCol md="12">
							<Controller
								name="textCorrection"
								control={control}
								rules={{
									required: "field is required",
								}}
								render={({ field }) => (
									<Input
										id="text-correction"
										{...field}
										autoFocus={false}
										type="text"
										placeholder="Text Correction"
										className="p-2"
										disabled={isRepliedCorrection}
										value={field.value}
										onChange={field.onChange}
										onBlur={(e) => handleBlur(field.name, field.value)}
									/>
								)}
							/>
							{errors?.textCorrection && <div className="error-msg mb-2">{errors.textCorrection.message}</div>}
						</MDBCol>
					)}
				</MDBRow>
				<MDBRow>
					<MDBCol md="12">
						<Label name="Message" className="mb-1 mt-1" />
					</MDBCol>
				</MDBRow>
				<MDBRow>
					<MDBCol md="12">
						<Controller
							name="replyMessage"
							control={control}
							defaultValue=""
							rules={{
								required: "field is required",
							}}
							render={({ field }) => (
								<Input
									id="reply_message"
									autoFocus={false}
									value={field.value}
									onChange={field.onChange}
									{...field}
									type="text"
									placeholder="Type your reply message here"
									className="p-2"
									onBlur={(e) => handleBlur(field.name, field.value)}
								/>
							)}
						/>
						{errors?.replyMessage && <div className="error-msg mb-2">{errors.replyMessage.message}</div>}
					</MDBCol>
				</MDBRow>
				{isRepliedCorrection ? (
					""
				) : (
					<>
						<MDBRow className="d-flex justify-space-between mb-3 mt-2">
							<MDBCol md="4">
								<Controller
									name="Reported Data"
									control={control}
									defaultValue={false}
									rules={{
										validate: {
											isRequired: () => isAtLeastOneChecked || "At least one checkbox must be selected",
										},
									}}
									render={({ field }) => (
										<AbsCheckBox
											{...field}
											id="Reported Data"
											slim={true}
											label="Reported Data"
											checked={field.value}
											handleChange={handleCheckboxChange(field)}
											disabled={isRepliedCorrection}
										/>
									)}
								/>
							</MDBCol>
						</MDBRow>
						{errors["Vessel Details"] && <div className="error-msg mb-2">{!isAtLeastOneChecked ? errors["Vessel Details"].message : ""}</div>}

						{errors?.[childReportsIdList[0]?.reportid]?.message && (
							<div className="error-msg mb-2">{!isAtLeastOneApplicableReportChecked ? errors[childReportsIdList[0]?.reportid].message : ""}</div>
						)}
					</>
				)}
				<div className="mt-5 mb-2 corrections-save-cancel-btn-wrapper">
					<div className="mt-3">
						<div className="d-flex justify-content-end">
							<button className="btn mr-2 enroll-submit-btn" disabled={disableButtons} type="submit">
								Save
							</button>
							<button
								id="enroll_vessel_cancel_button"
								className="btn fleet-cancel-btn"
								disabled={disableButtons}
								type="button"
								onClick={toggleCorrection}
							>
								Cancel
							</button>
						</div>
					</div>
				</div>
			</form>
		</div>
	);
};

export default CorrectionRequestForm;
